import type { TFunction } from 'i18next';
import { FundOverdraftSummaryDtoIn } from '@/models/in/FundBasicDetailsDtoIn';

/** End date the legacy admin screen writes for "no end date"; shown as such rather than as a year. */
const NO_END_DATE_YEAR = '2100';

export const formatFeatureDate = (value: string, t: TFunction): string => {
  if (value.slice(0, 4) === NO_END_DATE_YEAR) return t('fundsManagement.noEndDate');
  return new Date(value).toLocaleDateString('he-IL');
};

export const formatOverdraftAmount = (amount: number, language: string): string =>
  amount.toLocaleString(language, { style: 'currency', currency: 'ILS', maximumFractionDigits: 0 });

/**
 * One line describing a fund's frame, for the board cell and the Excel export alike:
 * "‎-8,000 ₪ · עד 25/03/2027", with "לא בתוקף" appended when today is outside its dates.
 * Empty for a fund with no frame, so the export cell stays blank.
 */
export const describeOverdraft = (overdraft: FundOverdraftSummaryDtoIn | null | undefined, t: TFunction, language: string): string => {
  if (!overdraft) return '';
  const parts = [formatOverdraftAmount(overdraft.amount, language), t('fundsManagement.overdraftUntil', { date: formatFeatureDate(overdraft.endDate, t) })];
  if (!overdraft.isCurrentlyActive) parts.push(t('fundsManagement.overdraftInactive'));
  return parts.join(' · ');
};

/**
 * Sort key for the frame column: funds with no frame first (ascending), then every fund that has
 * one, by the size of the frame regardless of its sign - a positive frame of 5,000 and a minus
 * frame of 5,000 are the same size of exception, and that is what the office compares.
 *
 * A fund with no frame sorts below zero rather than at zero, because a frame of exactly 0 is a
 * real case: the office records one with no amount purely to hang a reason on the fund ("ממתין
 * לחשבונית"). Scoring both at 0 scattered those funds through the thousands of frameless ones.
 */
export const overdraftSortValue = (overdraft: FundOverdraftSummaryDtoIn | null | undefined): number => (overdraft ? Math.abs(overdraft.amount) : -1);
